import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Input,
  Select,
  Button,
  TopProgress,
  CheckBox,
} from '../../../../../components';
import {
  Container,
  CreateContainer,
} from './styles';
import * as yup from 'yup';
import { useFormik } from 'formik';
import { useUi } from '../../../../../context/ui.context';
import { openStackService } from '../../../../../api';

const OpenStackSettings: FC = () => {
  const [t] = useTranslation();
  const { viewAlert } = useUi();
  const [initialValues, setInitialValues] = useState<any>({
    default_roles: [{ value: '_member_', label: '_member_' }],
    default_roles_standard_user: [{ value: '_member_', label: '_member_' }],
    default_region: 'default',
    default_project_name: '{{ client.id }}',
    default_project_description: 'Billing UA created for {{ client.name }}',
    project_domain_id: 'default',
    timeout: 60,
    gnocchi_resource_types_timeout: 5,
    hide_projects_and_api_users: false,
    use_api_username_template: false,
    api_username_template: '{{ user.email }}_{{ api_user_username }}',
    force_config_drive_for_instance_creation: false,
    hide_images_in_other_flavor_scope: false,
    hide_unassigned_images: false,
    use_instance_az_for_volume_on_instance_create: true,
    default_lbaas_region: 'default',
    retrieve_gnocchi_metadata: true,
    default_gnocchi_region: 'default',
    ptr_records_force_lowercase: false,
  });
  const [pageLoading, setPageLoading] = useState<boolean>(true);
  const [settings, setSettings] = useState<any>();
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingComplete, setLoadingComplete] = useState<boolean>(false);
  const [roleOptions, setRoleOptions] = useState<any[]>([]);
  const [regionOptions, setRegionOptions] = useState<any[]>([]);

  const onSubmit = async (values: any) => {
    setLoading(true);
    setLoadingComplete(false);
    
    try {
      // Преобразуем массивы объектов ролей обратно в массивы строк для API
      const submitData = {
        ...values,
        default_roles: Array.isArray(values.default_roles) 
          ? values.default_roles.map((role: any) => typeof role === 'string' ? role : role.value)
          : values.default_roles,
        default_roles_standard_user: Array.isArray(values.default_roles_standard_user)
          ? values.default_roles_standard_user.map((role: any) => typeof role === 'string' ? role : role.value)
          : values.default_roles_standard_user,
      };
      
      await openStackService.updateDefaultsSettings(submitData);
      
      setLoadingComplete(true);
      viewAlert({
        severity: 'success',
        message: t('SETTINGS_SAVED_SUCCESSFULLY'),
      });
      
      setTimeout(() => {
        setLoadingComplete(false);
      }, 2000);
    } catch (e: any) {
      viewAlert({
        severity: 'error',
        message: e?.response?.data?.detail || t('ERROR_OCCURED'),
      });
    } finally {
      setLoading(false);
    }
  };

  const {
    values,
    errors,
    touched,
    setErrors,
    handleSubmit,
    handleChange,
    handleBlur,
    setFieldValue,
  } = useFormik({
    enableReinitialize: true,
    validationSchema: yup.object().shape({
      default_roles: yup.array().min(1, `${t('REQUIRED_FIELD')}`).required(`${t('REQUIRED_FIELD')}`),
      default_roles_standard_user: yup.array().min(1, `${t('REQUIRED_FIELD')}`).required(`${t('REQUIRED_FIELD')}`),
      default_region: yup.string().required(`${t('REQUIRED_FIELD')}`),
      default_project_name: yup.string().required(`${t('REQUIRED_FIELD')}`),
      default_project_description: yup.string().required(`${t('REQUIRED_FIELD')}`),
      project_domain_id: yup.string().required(`${t('REQUIRED_FIELD')}`),
      timeout: yup.number().required(`${t('REQUIRED_FIELD')}`),
      gnocchi_resource_types_timeout: yup.number().required(`${t('REQUIRED_FIELD')}`),
      hide_projects_and_api_users: yup.bool(),
      use_api_username_template: yup.bool(),
      api_username_template: yup.string().required(`${t('REQUIRED_FIELD')}`),
      force_config_drive_for_instance_creation: yup.bool(),
      hide_images_in_other_flavor_scope: yup.bool(),
      hide_unassigned_images: yup.bool(),
      use_instance_az_for_volume_on_instance_create: yup.bool(),
      default_lbaas_region: yup.string().required(`${t('REQUIRED_FIELD')}`),
      retrieve_gnocchi_metadata: yup.bool(),
      default_gnocchi_region: yup.string().required(`${t('REQUIRED_FIELD')}`),
      ptr_records_force_lowercase: yup.bool(),
    }),
    initialValues: {
      ...initialValues,
    },
    onSubmit: (values: any) => onSubmit(values),
  });

  const formikProps = {
    errors,
    values,
    touched,
    handleChange,
    handleBlur,
  };

  const getData = async () => {
    try {
      const res = await openStackService.getDefaultsSettings();
      const data = res.data;
      
      // Формируем опции для селектов ролей
      if (data.available_role_options) {
        const roles = data.available_role_options.map((role: any) => ({
          value: role.name,
          label: role.name,
        }));
        setRoleOptions(roles);
      }
      
      // Формируем опции для селектов регионов
      if (data.available_region_options) {
        const regions = data.available_region_options.map((region: any) => ({
          value: region.id,
          label: region.id,
        }));
        setRegionOptions(regions);
      }
      
      setSettings(data);
      
      // Убираем available_*_options из initialValues, чтобы не отправлять их обратно
      const { available_role_options, available_region_options, ...settingsForForm } = data;
      
      // Преобразуем массивы строк ролей в массивы объектов для мульти-селектов
      if (settingsForForm.default_roles && Array.isArray(settingsForForm.default_roles)) {
        settingsForForm.default_roles = settingsForForm.default_roles.map((role: string) => ({
          value: role,
          label: role,
        }));
      }
      
      if (settingsForForm.default_roles_standard_user && Array.isArray(settingsForForm.default_roles_standard_user)) {
        settingsForForm.default_roles_standard_user = settingsForForm.default_roles_standard_user.map((role: string) => ({
          value: role,
          label: role,
        }));
      }
      
      setInitialValues(settingsForForm);
      
      setPageLoading(false);
    } catch (e: any) {
      viewAlert({
        severity: 'error',
        message: e?.response?.data?.detail || t('ERROR_OCCURED'),
      });
    }
  };

  useEffect(() => {
    getData();
  }, []);

  if (pageLoading) return <TopProgress loading={pageLoading} />;

  return (
    <CreateContainer>
      <Container>
        <div style={{ display:'flex', flexDirection:'column', gap:'20px',marginTop:'20px', marginBottom:'20px'}}>
          <Select
            required
            data={roleOptions}
            selectedValue={values['default_roles']}
            onChange={(clickedItem) => {
              const currentValues = values['default_roles'] || [];
              const exists = currentValues.find((item: any) => item.value === clickedItem.value);
              if (exists) {
                // Удаляем элемент
                setFieldValue('default_roles', currentValues.filter((item: any) => item.value !== clickedItem.value));
              } else {
                // Добавляем элемент
                setFieldValue('default_roles', [...currentValues, clickedItem]);
              }
            }}
            multi
            title={t('DEFAULT_ROLES')}
          />
          <Select
            required
            data={roleOptions}
            selectedValue={values['default_roles_standard_user']}
            onChange={(clickedItem) => {
              const currentValues = values['default_roles_standard_user'] || [];
              const exists = currentValues.find((item: any) => item.value === clickedItem.value);
              if (exists) {
                // Удаляем элемент
                setFieldValue('default_roles_standard_user', currentValues.filter((item: any) => item.value !== clickedItem.value));
              } else {
                // Добавляем элемент
                setFieldValue('default_roles_standard_user', [...currentValues, clickedItem]);
              }
            }}
            multi
            title={t('DEFAULT_ROLES_STANDARD_USER')}
          />
          <Select
            required
            data={regionOptions}
            selectedValue={values['default_region']}
            onChange={(e) => setFieldValue('default_region', e)}
            title={t('DEFAULT_REGION')}
          />
          <Input
            required
            fieldName='default_project_name'
            title={t('DEFAULT_PROJECT_NAME')}
            placeholder=''
            {...formikProps}
          />
          <Input
            required
            fieldName='default_project_description'
            title={t('DEFAULT_PROJECT_DESCRIPTION')}
            placeholder=''
            {...formikProps}
          />
          <Input
            required
            fieldName='project_domain_id'
            title={t('PROJECT_DOMAIN_ID')}
            placeholder=''
            {...formikProps}
          />
          <Input
            required
            fieldName='timeout'
            title={t('TIMEOUT')}
            placeholder=''
            type='number'
            {...formikProps}
          />
          <Input
            required
            fieldName='gnocchi_resource_types_timeout'
            title={t('GNOCCHI_RESOURCE_TYPES_TIMEOUT')}
            placeholder=''
            type='number'
            {...formikProps}
          />
          <CheckBox
            value={values['hide_projects_and_api_users']}
            onChange={() => setFieldValue('hide_projects_and_api_users', !values['hide_projects_and_api_users'])}
            title={t('HIDE_PROJECTS_AND_API_USERS')}
          />
          <CheckBox
            value={values['use_api_username_template']}
            onChange={() => setFieldValue('use_api_username_template', !values['use_api_username_template'])}
            title={t('USE_API_USERNAME_TEMPLATE')}
          />
          <Input
            required
            fieldName='api_username_template'
            title={t('API_USERNAME_TEMPLATE')}
            placeholder=''
            {...formikProps}
          />
          <CheckBox
            value={values['force_config_drive_for_instance_creation']}
            onChange={() =>
              setFieldValue(
                'force_config_drive_for_instance_creation',
                !values['force_config_drive_for_instance_creation'],
              )
            }
            title={t('FORCE_CONFIG_DRIVE_FOR_INSTANCE_CREATION')}
          />
          <CheckBox
            value={values['hide_images_in_other_flavor_scope']}
            onChange={() =>
              setFieldValue('hide_images_in_other_flavor_scope', !values['hide_images_in_other_flavor_scope'])
            }
            title={t('HIDE_IMAGES_IN_OTHER_FLAVOR_SCOPE')}
          />
          <CheckBox
            value={values['hide_unassigned_images']}
            onChange={() => setFieldValue('hide_unassigned_images', !values['hide_unassigned_images'])}
            title={t('HIDE_UNASSIGNED_IMAGES')}
          />
          <CheckBox
            value={values['use_instance_az_for_volume_on_instance_create']}
            onChange={() =>
              setFieldValue(
                'use_instance_az_for_volume_on_instance_create',
                !values['use_instance_az_for_volume_on_instance_create'],
              )
            }
            title={t('USE_INSTANCE_AZ_FOR_VOLUME_ON_INSTANCE_CREATE')}
          />
          <Select
            required
            data={regionOptions}
            selectedValue={values['default_lbaas_region']}
            onChange={(e) => setFieldValue('default_lbaas_region', e)}
            title={t('DEFAULT_LBAAS_REGION')}
          />
          <CheckBox
            value={values['retrieve_gnocchi_metadata']}
            onChange={() => setFieldValue('retrieve_gnocchi_metadata', !values['retrieve_gnocchi_metadata'])}
            title={t('RETRIEVE_GNOCCHI_METADATA')}
          />
          <Select
            required
            data={regionOptions}
            selectedValue={values['default_gnocchi_region']}
            onChange={(e) => setFieldValue('default_gnocchi_region', e)}
            title={t('DEFAULT_GNOCCHI_REGION')}
          />
          <CheckBox
            value={values['ptr_records_force_lowercase']}
            onChange={() => setFieldValue('ptr_records_force_lowercase', !values['ptr_records_force_lowercase'])}
            title={t('PTR_RECORDS_FORCE_LOWERCASE')}
          />
        </div>
        <div style={{ display: 'flex', marginTop: '20px' }}>
          <Button
            variant='primary'
            size='display'
            onClick={() => handleSubmit()}
            title={t('SAVE')}
            loading={loading}
            loadingComplete={loadingComplete}
          />
        </div>
      </Container>
    </CreateContainer>
  );
};

export default OpenStackSettings;